import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { combineLatest, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { DealInsuranceService, DealService, VehicleService, WarrantyUiService } from "../../../../services";
import { WARRANTY_SETTINGS } from "../../warranty-config";
import { InsuranceProduct, WarrantySettingOption, WarrantyUi } from "../../../../models";
import { Actions } from "@ngrx/effects";
import { TermCost } from "src/app/settings-module/models";
import { HistoryService } from "../../../../services/history.service";

@Component({
  selector: "app-warranty-form",
  templateUrl: "./warranty-form.component.html",
  styleUrls: ["./warranty-form.component.scss"]
})
export class WarrantyFormComponent implements OnInit, OnDestroy {
  @Input() warrantyUi: WarrantyUi;
  private unsubscribe$ = new Subject();
  private uiState = {showCalculator: false};
  private appData = {insuranceProducts: []};
  private dealData = {insuranceProducts: []};
  private vehicleInsuranceProducts: InsuranceProduct[];
  private previousPlannedLengthOfOwnership
  private previousMilesDrivenPerYear
  private previousProductValue: Map<string, string> = new Map()
  private userHasInteractedWithVSC = false;

  vehicleNeedsForm: UntypedFormGroup = this.formBuilder.group({
    // Select Values Return As String
    plannedLengthOfOwnership: ["0"],
    milesDrivenPerYear: ["0"]
  });

  insuranceProductsForm: UntypedFormGroup = this.formBuilder.group({
    // Dynamically Set [productKey]: termCostIndex
  });

  constructor(
    private formBuilder: UntypedFormBuilder,
    private dealService: DealService,
    private warrantyUiService: WarrantyUiService,
    private vehicleService: VehicleService,
    private actions$: Actions,
    private historyService: HistoryService,
    private dealInsuranceService: DealInsuranceService,
  ) { }

  ngOnInit() {
    this.syncSelectedVehicleNeeds();
    this.syncInsuranceProducts();
    this.autoSubmitVehicleNeedsForm();
    // this.listenForVehicleNeedsChanges();
  }

  ngOnDestroy() {
    this.autoSubmitVehicleNeedsForm();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  // INITIALIZATION

  private syncSelectedVehicleNeeds() {
    this.previousPlannedLengthOfOwnership = this.warrantyUi.ownershipYears
    this.previousMilesDrivenPerYear = this.warrantyUi.milesPerYear
    console.log("Syncing Selected Vehicle Needs", {
      plannedLengthOfOwnership: this.warrantyUi.ownershipYears,
      milesDrivenPerYear: this.warrantyUi.milesPerYear
    });
    this.vehicleNeedsForm.patchValue({
      plannedLengthOfOwnership: this.warrantyUi.ownershipYears,
      milesDrivenPerYear: this.warrantyUi.milesPerYear
    });
  }

  private syncInsuranceProducts() {
    combineLatest([
      this.vehicleService.selectInsuranceProducts(),
      this.dealInsuranceService.selectInsuranceProducts()
    ])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([insuranceProducts, selectedProducts]) => {
        this.vehicleInsuranceProducts = insuranceProducts;
        this.initWarrantyForm(insuranceProducts, selectedProducts);
      });

  }

  // listenForVehicleNeedsChanges() {
  //   this.actions$.pipe(
  //     takeUntil(this.unsubscribe$),
  //     ofType(dealActions.setVehicleNeeds),
  //   ).subscribe(() => {
  //     const selectedProducts = this.appData.insuranceProducts;
  //     this.initWarrantyForm(this.vehicleInsuranceProducts, selectedProducts);
  //   });
  // }

  initWarrantyForm(insuranceProducts, selectedProducts) {
    console.log('=================== initWarrantyForm START ===================');
    this.dealData.insuranceProducts = selectedProducts || [];
    this.insuranceProductsForm = this.formBuilder.group({});
    this.appData.insuranceProducts = [];
    console.log('initWarrantyForm called', {
      insuranceProductsCount: insuranceProducts?.length,
      selectedProductsCount: selectedProducts?.length,
      productGraphsCount: this.warrantyUi?.productGraphs?.length,
      selectedProducts: selectedProducts
    });
    this.warrantyUi.productGraphs.forEach(graph => {
      const {productKey} = graph.product;
      const product = insuranceProducts.find(item => item.productKey === productKey);
      const selectedProduct = this.selectedProducts.find(item => item.productKey === productKey);
      console.log(`Processing product ${productKey}:`, {
        hasProduct: !!product,
        hasSelectedProduct: !!selectedProduct,
        userHasInteractedWithVSC: this.userHasInteractedWithVSC
      });
      // Check if there's a valid selected product with a valid term cost
      // For VSC, only use stored selection if user has interacted with it
      // Also check if selectedTerm is the last index (likely a default value from backend)
      const isLastIndex = selectedProduct?.selectedTerm === (product?.termCosts?.length - 1);
      const shouldUseStoredSelection = selectedProduct && 
        selectedProduct.selectedTerm !== undefined && 
        selectedProduct.selectedTerm !== null
      
      if (this.isVSCProduct(product) && selectedProduct && !this.userHasInteractedWithVSC) {
        console.log(`VSC: Ignoring stored selectedTerm ${selectedProduct.selectedTerm} because user hasn't interacted yet`);
      }
      
      if (this.isVSCProduct(product) && isLastIndex) {
        console.log(`VSC: Ignoring selectedTerm ${selectedProduct?.selectedTerm} (last index of ${product?.termCosts?.length} items) - likely backend default`);
      }
      
      if (shouldUseStoredSelection) {
        const {selectedTerm} = selectedProduct;
        const selectedTermCost = product.termCosts[ selectedTerm ];
        
        // Only use the selection if the selectedTermCost actually exists
        if (selectedTermCost) {
          let termCostIndex;
          
          console.log(`Product ${productKey} has valid selection (term ${selectedTerm}):`, {
            selectedTerm,
            selectedTermCost,
            termCostExists: !!selectedTermCost,
            termCostDetails: selectedTermCost ? {
              term: selectedTermCost.term,
              miles: selectedTermCost.miles,
              productName: (selectedTermCost as any).productName
            } : null
          });
          
          // If VSC product, find index in sorted array
          if (this.isVSCProduct(product) && this.hasProductNames(product) && selectedTermCost?.productName) {
            const sortedTermCosts = this.getSortedTermCosts(product);
            // selectedTermCost is from the original array at position selectedTerm
            // We need to find where this term cost appears in the sorted array
            const sortedIndex = sortedTermCosts.findIndex((termCost: any) => {
              return selectedTermCost?.term === termCost?.term && 
                     selectedTermCost?.miles === termCost?.miles &&
                     selectedTermCost?.price === termCost?.price &&
                     (selectedTermCost as any)?.productName === termCost?.productName;
            });
            termCostIndex = sortedIndex >= 0 ? sortedIndex : 0;
            console.log(`VSC: Converting actual index ${selectedTerm} to sorted index ${termCostIndex}`);
          } else {
            // Original logic for non-VSC products
            termCostIndex = product.termCosts.findIndex((termCost: TermCost) => {
              return selectedTermCost?.term === termCost?.term && selectedTermCost?.miles === termCost?.miles;
            });
          }

          this.insuranceProductsForm.addControl(productKey, new UntypedFormControl(termCostIndex));
          
          // For the label, we're already using the correct index (actual for non-VSC, need to keep actual for VSC too)
          // Since selectedTerm is the actual index, we use that for the label
          this.previousProductValue.set(productKey, this.getProductLabel(product, selectedTerm))
          this.appData.insuranceProducts.push(product);
        } else if (product) {
          // selectedTermCost doesn't exist, treat as no selection
          console.log(`Product ${productKey} has invalid/no selection (selectedTerm: ${selectedProduct.selectedTerm})`);
          // Fall through to default selection logic below
          this.setDefaultSelection(product, productKey);
        }
      } else if (product) {
        // No selected product at all, use default selection
        this.setDefaultSelection(product, productKey);
      }
    });
    console.log('=================== initWarrantyForm END ===================');
  }

  private setDefaultSelection(product: InsuranceProduct, productKey: string) {
    // Initialize with default index
    let defaultIndex = 0;
    
    if (this.isVSCProduct(product) && this.hasProductNames(product)) {
      // For VSC products with groups, select the last item in the first group
      const sortedTermCosts = this.getSortedTermCosts(product);
      const groupedTermCosts = this.getGroupedTermCosts(product);
      
      console.log('Init Warranty Form - VSC Product (default selection):', {
        productKey,
        groupCount: groupedTermCosts.length,
        groups: groupedTermCosts.map(g => ({
          name: g.productName,
          itemCount: g.termCosts.length,
          firstItemIndex: g.termCosts[0]?.index,
          lastItemIndex: g.termCosts[g.termCosts.length - 1]?.index
        }))
      });
      
      if (groupedTermCosts.length > 0 && groupedTermCosts[0].termCosts.length > 0) {
        const firstGroup = groupedTermCosts[0];
        const lastItemInFirstGroup = firstGroup.termCosts[firstGroup.termCosts.length - 1];
        defaultIndex = lastItemInFirstGroup.index;
        
        console.log('Selected last item in first group for display only (not saving):', {
          groupName: firstGroup.productName,
          itemCount: firstGroup.termCosts.length,
          selectedIndex: defaultIndex,
          selectedTerm: lastItemInFirstGroup.termCost.term,
          selectedMiles: lastItemInFirstGroup.termCost.miles
        });
      }
    }
    
    // Set the form control value for display
    this.insuranceProductsForm.addControl(productKey, new UntypedFormControl(defaultIndex));
    
    // For VSC, don't save the default selection - let VSA modal handle its own default
    // Only save non-VSC product defaults
    if (!this.isVSCProduct(product)) {
      // For non-VSC products, save the default selection
      const labelIndex = this.getTermCostActualIndex(product, defaultIndex);
      this.previousProductValue.set(productKey, this.getProductLabel(product, labelIndex));
      // Note: Not calling warrantyUiService.selectInsuranceProduct here
      // We only save when user actually makes a selection
    } else {
      console.log('VSC default not saved - VSA modal will use its own default until user interacts');
    }
    
    this.appData.insuranceProducts.push(product);
  }

  // FORM HELPERS

  autoSubmitVehicleNeedsForm(changedItem: string = '') {
    // if (this.vehicleNeedsForm.pristine) { return; }
    let {milesDrivenPerYear, plannedLengthOfOwnership} = this.vehicleNeedsForm.value;
    milesDrivenPerYear = parseInt(milesDrivenPerYear, 10);
    plannedLengthOfOwnership = parseInt(plannedLengthOfOwnership, 10);

    this.warrantyUiService.selectWarrantySettingOptions(
      +plannedLengthOfOwnership,
      +milesDrivenPerYear
    );

    this.warrantyUiService.updateGapTerm(plannedLengthOfOwnership * 12, this.selectedProducts);

    if (changedItem === 'plannedLengthOfOwnership') {
      this.historyService.dispatchAddEvent({
        shortName: "Planned Length of Ownership Changed",
        oldValue: this.getPlannedLengthOfOwnershipLabel(this.previousPlannedLengthOfOwnership),
        newValue: this.getPlannedLengthOfOwnershipLabel(plannedLengthOfOwnership)
      });
    } else if (changedItem === 'milesDrivenPerYear') {
      this.historyService.dispatchAddEvent({
        shortName: "Miles Driven per Year Changed",
        oldValue: this.getMilesDrivenPerYearLabel(this.previousMilesDrivenPerYear),
        newValue: this.getMilesDrivenPerYearLabel(milesDrivenPerYear)
      });
    }
    this.previousPlannedLengthOfOwnership = plannedLengthOfOwnership
    this.previousMilesDrivenPerYear = milesDrivenPerYear

    this.vehicleNeedsForm.markAsPristine();
    // this.dealService.dispatchChangeDeal();
    this.dealService.dispatchSetVehicleNeeds({plannedLengthOfOwnership, milesDrivenPerYear});
  }

  getPlannedLengthOfOwnershipLabel(value: any): string {
    if (!this.yearsOptions || !this.yearsOptions.length) return '';
    for (let i = 0; i < this.yearsOptions.length; i++) {
      if (this.yearsOptions[ i ].years == value) return this.yearsOptions[ i ].title;
    }
    return '';
  }

  getMilesDrivenPerYearLabel(value: any): string {
    if (!this.milesOptions || !this.milesOptions.length) return '';
    for (let i = 0; i < this.milesOptions.length; i++) {
      if (this.milesOptions[ i ].miles == value) return this.milesOptions[ i ].title;
    }
    return '';
  }

  getProductLabel(product: InsuranceProduct, termCostIndex: any): string {
    if (!product || !product.termCosts) return '';
    const option = product.termCosts[ termCostIndex ]
    return `${option.term / 12} Years / ${option.miles >= 999999 ? 'Unlimited' : (option.miles)} Miles`
  }

  autoSubmitInsuranceProductsForm(product: InsuranceProduct) {
    if (this.insuranceProductsForm.pristine) { return; }
    const {productKey} = product;
    // Convert to number since form values come as strings
    let sortedIndex = +this.insuranceProductsForm.value[ productKey ];
    let actualIndex = sortedIndex;
    
    // Track that user has interacted with VSC
    if (this.isVSCProduct(product)) {
      this.userHasInteractedWithVSC = true;
      product.userSelection = true;
      console.log('User interacted with VSC dropdown - future selections will use stored values');
    }
    
    console.log('autoSubmitInsuranceProductsForm - START:', {
      productKey,
      formValue: this.insuranceProductsForm.value[ productKey ],
      sortedIndex: sortedIndex,
      isVSC: this.isVSCProduct(product),
      hasNames: this.hasProductNames(product),
      userHasInteractedWithVSC: this.userHasInteractedWithVSC
    });

    // Convert from sorted index to actual index for VSC products
    if (this.isVSCProduct(product) && this.hasProductNames(product)) {
      actualIndex = this.getTermCostActualIndex(product, sortedIndex);
      console.log('autoSubmitInsuranceProductsForm - After conversion:', {
        sortedIndex: sortedIndex,
        actualIndex: actualIndex
      });
      if (actualIndex < 0) {
        console.warn('Failed to convert index, using sorted index as fallback');
        actualIndex = sortedIndex; // Fallback if conversion fails
      }
    }

    // Get the actual term cost
    const selectedTermCost = product.termCosts[actualIndex];
    console.log('Selected term cost:', {
      actualIndex,
      term: selectedTermCost?.term,
      miles: selectedTermCost?.miles,
      productName: (selectedTermCost as any)?.productName
    });

    let productNameChanged = "";

    if (product.name.indexOf("Prepaid Maintenance") > -1) {
      productNameChanged = "PPM Changed"
    } else if (product.name.indexOf("Clear Care Elite") > -1) {
      productNameChanged = "CCE Changed"
    } else if (product.name.indexOf("Vehicle Service Agreement") > -1) {
      productNameChanged = "VSA Changed"
    }

    if (productNameChanged) {
      if (product?.termCosts[ actualIndex ]) {
        const newValue = this.getProductLabel(product, actualIndex);
        this.historyService.dispatchAddEvent({
          shortName: productNameChanged,
          oldValue: this.previousProductValue.get(productKey),
          newValue
        });
        this.previousProductValue.set(productKey, newValue)
      }
    }

    // Mark as pristine BEFORE calling selectInsuranceProduct to avoid re-trigger
    this.insuranceProductsForm.markAsPristine();
    
    console.log('Calling selectInsuranceProduct with actualIndex:', actualIndex);
    this.warrantyUiService.selectInsuranceProduct(product, actualIndex);
    
    this.dealService.dispatchChangeDeal();
    
    console.log('autoSubmitInsuranceProductsForm - END');
  }

  // UI CONTROL & RENDERING

  onToggleCalculator() {
    this.uiState.showCalculator = !this.uiState.showCalculator;
  }

  get showVehicleNeedsForm(): boolean {
    return this.warrantyUi.activeViewIndex > 0;
  }

  get showInsuranceProductsForm(): boolean {
    const {activeViewIndex, maxViewIndex} = this.warrantyUi;
    return activeViewIndex === maxViewIndex;
  }

  get showCalculator(): boolean {
    return this.uiState.showCalculator;
  }

  get milesOptions(): WarrantySettingOption[] {
    return WARRANTY_SETTINGS.milesDrivenPerYear || [];
  }

  get yearsOptions(): WarrantySettingOption[] {
    return WARRANTY_SETTINGS.plannedLengthOfOwnership || [];
  }

  get insuranceProducts(): InsuranceProduct[] {
    return this.appData.insuranceProducts || [];
  }

  get selectedProducts(): InsuranceProduct[] {
    return this.dealData.insuranceProducts || [];
  }

  // VSC Product Helpers

  isVSCProduct(product: InsuranceProduct): boolean {
    return product?.productKey === 'VSC';
  }

  hasProductNames(product: InsuranceProduct): boolean {
    return product?.termCosts?.some(tc => tc.productName) || false;
  }

  getSortedTermCosts(product: InsuranceProduct): any[] {
    if (!product?.termCosts) return [];
    
    const terms = product.termCosts;
    
    // Filter out "Other" products
    const filteredTerms = terms.filter(tc => tc.productName !== 'Other');
    
    // If this is a VSC product with productNames, sort them
    if (this.isVSCProduct(product) && filteredTerms.some(tc => tc.productName)) {
      return this.sortVSCTermCosts(filteredTerms);
    }
    
    return filteredTerms.length > 0 ? filteredTerms : terms;
  }

  private sortVSCTermCosts(termCosts: any[]): any[] {
    const vehicleCondition = this.warrantyUi.vehicleCondition;
    
    const getProductPriority = (tc: any): number => {
      const productName = tc.productName || '';
      const normalized = productName.toLowerCase();
      const deductible = tc.deductible || 0;
      
      // Special sorting for certified vehicles
      if (vehicleCondition === 'certified') {
        if (normalized.includes('premier') && normalized.includes('deductible waiver') && !normalized.includes('flex')) return 1;
        if (normalized.includes('premier flex') && normalized.includes('deductible waiver') && !normalized.includes('certified')) return 2;
        if (normalized.includes('premier flex certified') && normalized.includes('deductible waiver')) return 3;
        if (normalized.includes('standard') && normalized.includes('deductible waiver')) return 4;
        return 99;
      }
      
      // Special sorting for used vehicles
      if (vehicleCondition === 'used') {
        if (normalized.includes('premier') && normalized.includes('deductible waiver') && !normalized.includes('flex')) return 1;
        if (normalized.includes('premier flex') && normalized.includes('deductible waiver')) return 2;
        if (normalized.includes('standard') && normalized.includes('deductible waiver')) return 3;
        if (normalized.includes('powertrain') && normalized.includes('deductible waiver')) return 4;
        return 99;
      }
      
      // Default sorting for other vehicle types
      if (normalized.includes('premier') && normalized.includes('deductible waiver') && !normalized.includes('suspension')) return 1;
      if (normalized.includes('premier') && normalized.includes('suspension') && normalized.includes('deductible waiver')) return 2;
      if (normalized.includes('premier') && deductible === 50) return 3;
      if (normalized.includes('premier') && deductible === 100) return 4;
      if (normalized.includes('standard') && normalized.includes('deductible waiver')) return 5;
      if (normalized.includes('standard') && deductible === 50) return 6;
      if (normalized.includes('standard') && deductible === 100) return 7;
      if (normalized.includes('premier flex certified')) return 8;
      if (normalized.includes('premier flex')) return 9;
      
      return 99;
    };
    
    return termCosts.sort((a, b) => {
      const priorityA = getProductPriority(a);
      const priorityB = getProductPriority(b);
      
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      
      // Then by term
      if (a.term !== b.term) {
        return a.term - b.term;
      }
      
      // Then by miles
      return (a.miles || 0) - (b.miles || 0);
    });
  }

  getGroupedTermCosts(product: InsuranceProduct): any[] {
    if (!product?.termCosts) return [];
    
    const groups = new Map<string, any[]>();
    const sortedTermCosts = this.getSortedTermCosts(product);
    
    sortedTermCosts.forEach((termCost, index) => {
      const productName = termCost.productName || 'Other';
      
      // Skip 'Other' products
      if (productName === 'Other') return;
      
      let groupKey = productName;
      
      // Add deductible to group name if not already included
      if ((productName.toLowerCase().includes('premier') || productName.toLowerCase().includes('standard')) 
          && !productName.includes('$') 
          && termCost.deductible > 0) {
        groupKey = `${productName} - $${termCost.deductible} Deductible`;
      }
      
      if (!groups.has(groupKey)) {
        groups.set(groupKey, []);
      }
      
      groups.get(groupKey).push({ termCost, index });
    });
    
    return Array.from(groups.entries()).map(([productName, termCosts]) => ({
      productName,
      termCosts
    }));
  }

  getTermCostActualIndex(product: InsuranceProduct, sortedIndex: number): number {
    const sortedTermCosts = this.getSortedTermCosts(product);
    if (sortedIndex < 0 || sortedIndex >= sortedTermCosts.length) {
      return -1;
    }
    
    const selectedTermCost = sortedTermCosts[sortedIndex];
    
    // Find this term cost in the original array
    return product.termCosts.findIndex(tc => 
      tc.term === selectedTermCost.term && 
      tc.miles === selectedTermCost.miles && 
      tc.price === selectedTermCost.price &&
      tc.productName === selectedTermCost.productName
    );
  }

}
